<template>
  <div ref="rootRef" :dir="isRtl !== false ? 'rtl' : 'ltr'" class="relative w-full custom-selector-container">
    <button
      type="button"
      :class="['btn relative w-full justify-start font-normal h-auto py-2 px-3 min-h-0', isRtl === true ? 'text-right' : 'text-left']"
      @click="toggleDropdown"
      aria-haspopup="listbox"
      :aria-expanded="isOpen !== false"
    >
      <div v-if="selectedOptionObj !== null" class="flex items-center w-full min-w-0" style="padding-inline-end: 1.75rem;">
        <div class="flex-grow overflow-hidden relative w-full" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">
          <span class="block text-sm font-medium truncate relative w-full">{{ getOptionLabel(selectedOptionObj) }}</span>
        </div>
      </div>
      <div v-else class="flex items-center w-full min-w-0" style="padding-inline-end: 1.75rem;">
        <span class="text-sm text-base-content opacity-70">{{ placeholder }}</span>
      </div>
      <Icon icon="heroicons:chevron-down-20-solid" class="pointer-events-none absolute top-1/2 h-5 w-5 -translate-y-1/2 text-base-content opacity-60" :class="isRtl === true ? 'left-3' : 'right-3'" />
    </button>

    <Teleport to="body">
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <div
          v-if="isOpen === true"
          ref="dropdownListRef"
          :dir="isRtl === true ? 'rtl' : 'ltr'"
          class="flex max-h-full min-w-0 flex-col overflow-hidden rounded-xl border border-base-300 bg-base-100 shadow-xl ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-800 dark:ring-white/10"
          :style="dropdownPanelStyle"
        >
        <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
          <button
            v-for="opt in options"
            :key="getOptionValue(opt)"
            type="button"
            class="mx-1 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150 ease-in-out"
            :class="[
              isRtl === true ? 'text-right' : 'text-left',
              getOptionValue(opt) === modelValue ? 'bg-primary text-primary-content shadow-sm' : 'text-base-content hover:bg-base-200 dark:hover:bg-gray-700'
            ]"
            @click="selectOptionAndClose(getOptionValue(opt))"
          >
            <span class="block truncate">{{ getOptionLabel(opt) }}</span>
          </button>
        </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import { useBodyAnchoredDropdown } from '../composables/useBodyAnchoredDropdown.js';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Select a Mode' },
  options: { type: Array, default: () => [] }

});
const emit = defineEmits(['update:modelValue']);

const { currentLanguage } = useI18n();
const isRtl = computed(() => (['he', 'ar'].includes(currentLanguage.value) === true) ? true : false);

const isOpen = ref(false);
const rootRef = ref(null);
const dropdownListRef = ref(null);
const { panelStyle: dropdownPanelStyle } = useBodyAnchoredDropdown(rootRef, isOpen, { capHeight: 240, zIndex: 265 });

function getOptionValue(option) {
  if (option !== null && typeof option === 'object') {
    return option.id || option.value || option.label || '';
  }
  return option;
}

function getOptionLabel(option) {
  if (option !== null && typeof option === 'object') {
    return option.label || option.name || option.id || option.value || '';
  }
  return option;
}

const selectedOptionObj = computed(() => {
  const found = props.options.find(opt => getOptionValue(opt) === props.modelValue);
  return found !== undefined ? found : null;
});

const toggleDropdown = () => { isOpen.value = isOpen.value !== true; };
const selectOptionAndClose = (optId) => {
  if (props.modelValue !== optId) {
    emit('update:modelValue', optId);
  }
  isOpen.value = false;
};

const handleClickOutside = (event) => {
  if (isOpen.value !== true) {
    return;
  }
  if (rootRef.value !== null && rootRef.value.contains(event.target) === true) {
    return;
  }
  if (dropdownListRef.value !== null && dropdownListRef.value.contains(event.target) === true) {
    return;
  }
  isOpen.value = false;
};

onMounted(() => { document.addEventListener('click', handleClickOutside, true); });
onBeforeUnmount(() => { document.removeEventListener('click', handleClickOutside, true); });
</script>

